import { Fragment, useRef, useState } from 'react';
import type { IJourneyDetails } from '../../../../../../../models/journey-details.model';
import type { IJourney } from '../../../../../../../models/journey.model';
import JourneySection from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/JourneySection';
import AccessLink from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/AccessLink';
import { LegHelper } from '../../../../../../../helpers/leg.helper';
import type { ITripLeg } from '../../../../../../../models/trip-leg.model';
import type { IConnectionLink } from '../../../../../../../models/connection-link.model';
import TripLeg from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/TripLeg';
import ConnectionLink from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/ConnectionLink';
import type { ITripLegDetails } from '../../../../../../../models/trip-leg-details.model';
import TripLegCalls from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/TripLegCalls';
import { LineHelper } from '../../../../../../../helpers/line.helper';
import DestinationLink from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/DestinationLink';
import Notes from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/TripLeg/components/Notes';
import type { IConnectionLinkDetails } from '../../../../../../../models/connection-link-details.model';
import { AnalyticsService } from '../../../../../../../services/analytics-service';
import { LineType } from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/LineMarker/line-markers.enum';
import LogService from '../../../../../../../services/log-service/log.service';
import AcccessLinkConnectionLinkDetails from '../../../../../../TravelPlanner/components/JourneyDetails/components/Details/components/AcccessLinkConnectionLinkDetails';
import type { ISelectedNonPublicTransportLink } from '../../../../../../TravelPlanner/components/JourneyDetails/models/selected-non-public-transport';
import { IAccessLink } from '../../../../../../../models/access-link.model';

function getDetailsForTripLeg(tripLeg: ITripLeg, journeyDetails: IJourneyDetails | undefined): ITripLegDetails | undefined {
  const tripLegDetails = journeyDetails?.tripLegs?.find((leg) => leg.journeyLegIndex === tripLeg.journeyLegIndex);
  if (tripLegDetails) return tripLegDetails;
  LogService.logError(new Error('Trip leg details not found'), 'No trip leg found in details with the matching index');
  return undefined;
}

function getDetailsForConnectionLink(connectionLink: IConnectionLink, journeyDetails: IJourneyDetails | undefined): IConnectionLinkDetails | undefined {
  return journeyDetails?.connectionLinks?.find((cl) => cl.journeyLegIndex === connectionLink.journeyLegIndex);
}

function getSelectedAccessLink(
  selectedNonPublicTransportLink: ISelectedNonPublicTransportLink | undefined,
  departureAccessLink: IAccessLink | undefined,
  arrivalAccessLink: IAccessLink | undefined,
): IAccessLink | undefined {
  if (!selectedNonPublicTransportLink) return undefined;
  if (selectedNonPublicTransportLink.departureAccessLink && departureAccessLink) return departureAccessLink;
  if (selectedNonPublicTransportLink.arrivalAccessLink && arrivalAccessLink) return arrivalAccessLink;
  return undefined;
}

function Details({ journey, journeyDetails }: { journey: IJourney; journeyDetails: IJourneyDetails }) {
  const [selectedTripLeg, setSelectedTripLeg] = useState<ITripLeg | undefined>(undefined);
  const [selectedNonPublicTransportLink, setSelectedNonPublicTransportLink] = useState<ISelectedNonPublicTransportLink | undefined>(undefined);

  const tripLegCallsRef = useRef<HTMLDivElement>(null);
  const connectionLinkDetailsRef = useRef<HTMLDivElement>(null);

  return (
    <div>
      {!selectedTripLeg && !selectedNonPublicTransportLink && (
        <ol className="journey-details-details__list list-unstyled overflow-hidden">
          {journey.departureAccessLink && (
            <JourneySection originEndpoint={journey.departureAccessLink.origin} lineType={LineType.dotted}>
              <AccessLink
                departureAccessLink={journey.departureAccessLink}
                onAccessLinkSelected={(x) => {
                  setSelectedNonPublicTransportLink(x);
                }}
              />
            </JourneySection>
          )}
          {journey.destinationLink && (
            <JourneySection originEndpoint={journey.destinationLink.origin} destinationEndpoint={journey.destinationLink.destination} lineType={LineType.dotted}>
              <DestinationLink destinationLink={journey.destinationLink} />
            </JourneySection>
          )}

          {journey.allLegs?.map((leg, index) => {
            if (LegHelper.isTripLeg(leg)) {
              const tripLeg = leg as ITripLeg;
              const nextTripLeg = journey.allLegs && index < journey.allLegs.length && LegHelper.isTripLeg(journey.allLegs[index + 1]) ? (journey.allLegs[index + 1] as ITripLeg) : undefined;
              const hasSamePlatform =
                nextTripLeg && nextTripLeg.origin.stopPoint.gid === tripLeg.destination.stopPoint.gid && nextTripLeg.origin.stopPoint.platform === tripLeg.destination.stopPoint.platform;
              const tripLegDetails = getDetailsForTripLeg(tripLeg, journeyDetails);
              const guaranteedChangeNotes = LegHelper.getGuaranteedChangeNotes(tripLeg);

              return (
                <Fragment key={`trip-leg-${leg.journeyLegIndex}`}>
                  <JourneySection
                    origin={tripLeg.origin}
                    destination={tripLeg.destination}
                    line={tripLeg.serviceJourney.line}
                    lineColor={LineHelper.getBackgroundSafeForegroundColor(tripLeg.serviceJourney.line)}
                    originDetails={tripLegDetails?.callsOnTripLeg ? tripLegDetails.callsOnTripLeg[0] : undefined}
                    destinationDetails={tripLegDetails?.callsOnTripLeg ? tripLegDetails.callsOnTripLeg[tripLegDetails.callsOnTripLeg.length - 1] : undefined}
                    className={hasSamePlatform ? 'mb-2' : ''}
                  >
                    {tripLeg && tripLegDetails && (
                      <TripLeg
                        tripLeg={tripLeg}
                        tripLegDetails={tripLegDetails}
                        onTriplegSelected={(tripleg) => {
                          setSelectedTripLeg(tripleg);
                          AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy delresa', '');
                        }}
                      />
                    )}
                  </JourneySection>
                  {guaranteedChangeNotes.length > 0 && (
                    <JourneySection lineType={LineType.none} className="mb-2">
                      <Notes notes={guaranteedChangeNotes} />
                    </JourneySection>
                  )}
                </Fragment>
              );
            }
            if (LegHelper.isConnectionLink(leg)) {
              const connectionLink = leg as IConnectionLink;
              const connectionLinkDetails = getDetailsForConnectionLink(connectionLink, journeyDetails);
              const guaranteedChangeNotes = LegHelper.getGuaranteedChangeNotes(leg);
              return (
                <JourneySection key={`connection-link-${leg.journeyLegIndex}`} lineType={LineType.dotted}>
                  {guaranteedChangeNotes.length > 0 && <Notes notes={guaranteedChangeNotes} className="mb-2" />}
                  {connectionLinkDetails && (
                    <ConnectionLink
                      connectionLinkDetails={connectionLinkDetails}
                      onConnectionLinkSelected={(s) => {
                        setSelectedNonPublicTransportLink(s);
                      }}
                    />
                  )}
                </JourneySection>
              );
            }
            return null;
          })}

          {journey.arrivalAccessLink && (
            <JourneySection destinationEndpoint={journey.arrivalAccessLink.destination} lineType={LineType.dotted}>
              <AccessLink
                arrivalAccessLink={journey.arrivalAccessLink}
                onAccessLinkSelected={(x) => {
                  setSelectedNonPublicTransportLink(x);
                }}
              />
            </JourneySection>
          )}
        </ol>
      )}
      {selectedTripLeg && LegHelper.isTripLeg(selectedTripLeg) && (
        <div className="mb-4" ref={tripLegCallsRef}>
          <TripLegCalls
            tripLeg={selectedTripLeg}
            tripLegDetails={getDetailsForTripLeg(selectedTripLeg, journeyDetails)}
            journeyId={journey.id}
            onClose={() => {
              setSelectedTripLeg(undefined);
            }}
          />
        </div>
      )}
      {selectedNonPublicTransportLink && (
        <div className="mb-4" ref={connectionLinkDetailsRef}>
          <AcccessLinkConnectionLinkDetails
            connectionLink={
              selectedNonPublicTransportLink.connectionLinkLegIndex !== undefined
                ? (journey.allLegs?.find((leg) => LegHelper.isConnectionLink(leg) && leg.journeyLegIndex === selectedNonPublicTransportLink.connectionLinkLegIndex) as IConnectionLink)
                : undefined
            }
            accessLink={getSelectedAccessLink(selectedNonPublicTransportLink, journey.departureAccessLink, journey.arrivalAccessLink)}
            nextTripLeg={
              selectedNonPublicTransportLink.connectionLinkLegIndex !== undefined
                ? (journey.allLegs?.find((leg) => LegHelper.isTripLeg(leg) && leg.journeyLegIndex === selectedNonPublicTransportLink.connectionLinkLegIndex! + 1) as ITripLeg)
                : undefined
            }
            journeyId={journey.id}
            onClose={() => {
              setSelectedNonPublicTransportLink(undefined);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Details;
